import fs from 'node:fs';
import path from 'node:path';
import { SKILL_DISCOVERY_ROOTS, yieldSkillDiscoveryWork } from './skill-discovery-roots.js';

export type SkillLayoutRoot = {
  path: string;
  skills: Array<{ name: string; relPath: string }>;
};

function collectLayoutSkill(
  rel: string,
  base: string,
  name: string,
  skills: SkillLayoutRoot['skills'],
): void {
  const dir = path.join(base, name);
  try {
    if (!fs.statSync(dir).isDirectory()) return;
  } catch {
    return;
  }
  if (!fs.existsSync(path.join(dir, 'SKILL.md'))) return;
  const relPath = `${rel}/${name}`.replace(/\\/g, '/');
  skills.push({ name, relPath });
}

/**
 * Skill roots with one entry per root that has at least one skill folder (for bridge layout UI).
 */
export function discoverSkillLayoutRoots(cwd: string): SkillLayoutRoot[] {
  const roots: SkillLayoutRoot[] = [];
  for (const rel of SKILL_DISCOVERY_ROOTS) {
    const base = path.join(cwd, rel);
    if (!fs.existsSync(base) || !fs.statSync(base).isDirectory()) continue;
    let entries: string[] = [];
    try {
      entries = fs.readdirSync(base);
    } catch {
      continue;
    }
    const skills: SkillLayoutRoot['skills'] = [];
    for (const name of entries) {
      collectLayoutSkill(rel, base, name, skills);
    }
    if (skills.length > 0) {
      roots.push({ path: rel.replace(/\\/g, '/'), skills });
    }
  }
  return roots;
}

/** Like {@link discoverSkillLayoutRoots}, yielding during filesystem scans. */
export async function discoverSkillLayoutRootsAsync(cwd: string): Promise<SkillLayoutRoot[]> {
  const roots: SkillLayoutRoot[] = [];
  let work = 0;

  for (const rel of SKILL_DISCOVERY_ROOTS) {
    await yieldSkillDiscoveryWork(++work);
    const base = path.join(cwd, rel);
    if (!fs.existsSync(base) || !fs.statSync(base).isDirectory()) continue;
    let entries: string[] = [];
    try {
      entries = fs.readdirSync(base);
    } catch {
      continue;
    }
    const skills: SkillLayoutRoot['skills'] = [];
    for (let i = 0; i < entries.length; i++) {
      await yieldSkillDiscoveryWork(++work);
      collectLayoutSkill(rel, base, entries[i]!, skills);
    }
    if (skills.length > 0) {
      roots.push({ path: rel.replace(/\\/g, '/'), skills });
    }
  }
  return roots;
}
